/**
 * Mobile Settings Modal
 * Settings screen for mobile app with notification controls
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { isNative, registerPushNotifications, resetPushRegistration, getPushToken, getPlatform } from '@/lib/capacitor';
import { apiFetch } from '@/lib/apiClient';
import { getAuthItem } from '@/lib/authStorage';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import './MobileSettingsModal.css';

interface MobileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileSettingsModal({ isOpen, onClose }: MobileSettingsModalProps) {
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [notificationTier, setNotificationTier] = useState<string>('normal');
  const [isLoadingTier, setIsLoadingTier] = useState(false);

  // Check if notifications are enabled on mount
  useEffect(() => {
    if (isOpen && isNative()) {
      const token = getPushToken();
      setNotificationsEnabled(!!token);
      setNotificationError(null);
    }
  }, [isOpen]);

  // Fetch current notification tier on mount
  useEffect(() => {
    if (!isOpen) return;
    const authToken = getAuthItem('xavior_auth_token') ?? undefined;
    if (!authToken) return;
    apiFetch('/auth/session', { authToken })
      .then((data: any) => {
        if (data?.notificationTier) {
          setNotificationTier(data.notificationTier);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  const handleToggleNotifications = async () => {
    if (isTogglingNotifications) return;
    setIsTogglingNotifications(true);
    setNotificationError(null);

    const authToken = getAuthItem('xavior_auth_token') ?? undefined;

    try {
      if (notificationsEnabled) {
        // Disable notifications - best-effort revoke from backend, always clear local state
        const token = getPushToken();
        if (token) {
          try {
            await apiFetch('/auth/push-token', {
              method: 'DELETE',
              body: JSON.stringify({ deviceToken: token }),
              authToken,
            });
          } catch {
            // Token may already be gone from backend — still clear local state
          }
        }
        // Reset registration state so re-enabling does a clean fresh registration
        resetPushRegistration();
        setNotificationsEnabled(false);
        return;
      } else {
        // Enable notifications - waits for the APNs token
        console.log('[settings] Enabling push notifications...');
        const token = await registerPushNotifications();
        console.log('[settings] Got token:', token ? 'yes' : 'no');

        if (token) {
          // Register token with backend - treat duplicate/conflict as success
          console.log('[settings] Registering token with backend...');
          try {
            await apiFetch('/auth/push-token', {
              method: 'POST',
              body: JSON.stringify({
                deviceToken: token,
                platform: getPlatform(),
              }),
              authToken,
            });
            console.log('[settings] Token registered successfully');
          } catch (backendError: any) {
            // 409 Conflict = token already registered, treat as success
            if (backendError?.status !== 409) {
              console.error('[settings] Backend registration failed:', backendError);
              throw backendError;
            }
            console.log('[settings] Token already registered on backend (409), treating as success');
          }
          setNotificationsEnabled(true);
        } else {
          console.log('[settings] No token received - notifications not enabled');
          setNotificationError('Could not enable push notifications. Please check your notification permissions in Settings and try again.');
        }
      }
    } catch (error) {
      console.error('[settings] Failed to toggle notifications:', error);
      const message = error instanceof Error && error.message === 'PERMISSION_DENIED'
        ? 'Notifications are blocked. Please open Settings → Notifications → XAVIOR and enable them.'
        : notificationsEnabled
          ? 'Failed to disable notifications. Please try again.'
          : 'Failed to enable notifications. Please try again.';
      setNotificationError(message);
    } finally {
      setIsTogglingNotifications(false);
    }
  };

  const handleTierChange = async (tier: string) => {
    if (isLoadingTier || tier === notificationTier) return;
    setIsLoadingTier(true);
    const authToken = getAuthItem('xavior_auth_token') ?? undefined;
    try {
      await apiFetch('/auth/notification-tier', {
        method: 'PATCH',
        body: JSON.stringify({ tier }),
        authToken,
      });
      setNotificationTier(tier);
    } catch (err) {
      console.error('Failed to update notification tier:', err);
    } finally {
      setIsLoadingTier(false);
    }
  };

  if (!isOpen) return null;

  const portalRoot = document.getElementById('modal-portal-root');

  const content = (
    <div className="mobile-settings-overlay" onMouseDown={handleOverlayMouseDown}
 onClick={handleOverlayClick}>
      <div ref={modalContentRef} className="mobile-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-settings-header">
          <h2>Settings</h2>
          <button className="mobile-settings-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mobile-settings-content">
          {/* Notifications Section */}
          <div className="mobile-settings-section">
            <h3 className="mobile-settings-section-title">Notifications</h3>

            {isNative() ? (
              <>
                <div className="mobile-settings-row">
                  <div className="mobile-settings-row-info">
                    <span className="mobile-settings-row-label">Push Notifications</span>
                    <span className="mobile-settings-row-description">
                      Get notified when AI responds or tasks are due
                    </span>
                  </div>
                  <button
                    className={`mobile-settings-toggle ${notificationsEnabled ? 'enabled' : ''}`}
                    onClick={handleToggleNotifications}
                    disabled={isTogglingNotifications}
                  >
                    <span className="mobile-settings-toggle-knob" />
                  </button>
                </div>
                {notificationError && (
                  <div className="mobile-settings-error">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4M12 16h.01" />
                    </svg>
                    {notificationError}
                  </div>
                )}
                {notificationsEnabled && (
                  <div className="mobile-settings-tier-selector">
                    <span className="mobile-settings-row-label" style={{ marginBottom: 10, display: 'block' }}>
                      Notification Level
                    </span>
                    <div className="mobile-settings-segmented-control">
                      {[
                        { value: 'quiet', label: 'Quiet', desc: 'Daily briefing only' },
                        { value: 'normal', label: 'Normal', desc: 'Briefings + alerts' },
                        { value: 'everything', label: 'Everything', desc: 'All notifications' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          className={`mobile-settings-segment ${notificationTier === opt.value ? 'active' : ''}`}
                          onClick={() => handleTierChange(opt.value)}
                          disabled={isLoadingTier}
                        >
                          <span className="mobile-settings-segment-label">{opt.label}</span>
                          <span className="mobile-settings-segment-desc">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="mobile-settings-info">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <span>Push notifications are only available in the mobile app</span>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );

  return portalRoot ? createPortal(content, portalRoot) : content;
}
